/** Global Settings keys visible when DISABLE_CORE_UI is ON */
export const AIEM_VISIBLE_GLOBAL_SETTINGS: string[] = ["step16", "step32"];

export const AIEM_ALWAYS_VISIBLE_INTEGRATIONS: string[] = [
  "github",
  "zscaler logs",
  "crowdstrike edr",
  "sentinelone edr",
  "armorcode api",
  "armorcode agent",
  "google workspace",
];

export const AIEM_SECURITY_TOOLS: string[] = ["zscaler"];

/** Widget types visible in Add Widget popup when DISABLE_CORE_UI is ON */
export const AIEM_VISIBLE_WIDGET_TYPES: string[] = [
  "AI Inventory",
  "Text Block",
];

/**
 * Chart type and order-by values used by AI Inventory widgets.
 * Defined locally to avoid circular dependency with CustomDashboardConstants
 * (which imports from this file). Values match TIME_LINE_WIDGET_CHART_TYPE and DonutOrderBy.
 */
const AI_CHART_TYPE = {
  AREA: "Area",
  LINE: "Line",
} as const;

const AI_DONUT_ORDER_BY = {
  COUNT_DESC: "countDesc",
} as const;

/** AI Inventory widget display types */
export const CUSTOM_WIDGET_AI_INVENTORY_DISPLAY_TYPE = {
  METRIC: "Metric",
  SIMPLE_TABLE: "Simple Table",
  DONUT_CHART: "Donut Chart",
  USAGE_DONUT: "Usage Donut",
  TIME_SERIES: "Time Series",
  USAGE_TIME_SERIES: "Usage Time Series",
  RISK_MATRIX: "Risk Matrix",
  BUBBLE_CHART: "Bubble Chart",
} as const;

/** Initial values for each AI Inventory display type */
export const WidgetTypeAIInventoryInitialValues = {
  [CUSTOM_WIDGET_AI_INVENTORY_DISPLAY_TYPE.METRIC]: {
    fontSize: 64,
    abbreviatedNumber: true,
    color: "#7839ED",
  },
  [CUSTOM_WIDGET_AI_INVENTORY_DISPLAY_TYPE.SIMPLE_TABLE]: {
    columns: ["riskLevel", "type", "status", "users"],
    maxResult: 6,
    sortBy: "riskLevel",
  },
  [CUSTOM_WIDGET_AI_INVENTORY_DISPLAY_TYPE.DONUT_CHART]: {
    showLegends: true,
    legendPosition: "right",
    legendDisplayValue: "count",
    orderBy: AI_DONUT_ORDER_BY.COUNT_DESC,
    showTotalGroups: false,
    column1: "risk_level",
  },
  [CUSTOM_WIDGET_AI_INVENTORY_DISPLAY_TYPE.USAGE_DONUT]: {
    topNInventories: 10,
    showLegends: true,
    legendPosition: "right",
    legendDisplayValue: "count",
  },
  [CUSTOM_WIDGET_AI_INVENTORY_DISPLAY_TYPE.TIME_SERIES]: {
    chartType: AI_CHART_TYPE.AREA,
    groupBy: "risk_level",
    aggregateBy: "day",
    timeRange: 30,
    metric: "inventory_count",
    showLegends: true,
  },
  [CUSTOM_WIDGET_AI_INVENTORY_DISPLAY_TYPE.USAGE_TIME_SERIES]: {
    chartType: AI_CHART_TYPE.AREA,
    groupBy: "risk_level",
    aggregateBy: "day",
    timeRange: 30,
    metric: "user_count",
    showLegends: true,
  },
  [CUSTOM_WIDGET_AI_INVENTORY_DISPLAY_TYPE.RISK_MATRIX]: {
    metric: "inventory_count",
  },
  [CUSTOM_WIDGET_AI_INVENTORY_DISPLAY_TYPE.BUBBLE_CHART]: {
    metric: "inventory_count",
  },
};

/** AI Inventory Donut Chart Group By Options */
export const AI_INVENTORY_DONUT_CHART_GROUP_BY_OPTIONS = [
  { label: "Risk Level", value: "risk_level" },
  { label: "Status", value: "status" },
  { label: "Source", value: "source" },
];

/** AI Inventory Time Series Chart Type Options */
export const AI_INVENTORY_TIME_SERIES_CHART_TYPE_OPTIONS = [
  { label: "Line", value: AI_CHART_TYPE.LINE },
  { label: "Area", value: AI_CHART_TYPE.AREA },
];

/** AI Inventory Time Series Group By Options */
export const AI_INVENTORY_TIME_SERIES_GROUP_BY_OPTIONS = [
  { label: "Risk Level", value: "risk_level" },
  { label: "Status", value: "status" },
];

/** AI Inventory Time Series Aggregate By Options */
export const AI_INVENTORY_TIME_SERIES_AGGREGATE_BY_OPTIONS = [
  { label: "Daily", value: "day" },
  { label: "Weekly", value: "week" },
  { label: "Monthly", value: "month" },
];

/** AI Inventory Time Series Time Range Options */
export const AI_INVENTORY_TIME_SERIES_TIME_RANGE_OPTIONS = [
  { label: "7 days", value: 7 },
  { label: "30 days", value: 30 },
  { label: "60 days", value: 60 },
  { label: "90 days", value: 90 },
];

/** AI Activity Time Series Metric Options */
export const AI_ACTIVITY_TIME_SERIES_METRIC_OPTIONS = [
  { label: "User Count", value: "user_count" },
  { label: "Usage Count", value: "usage_count" },
];

/** AI Inventory Risk Matrix / Bubble Chart Metric Options */
export const AI_INVENTORY_RISK_MATRIX_METRIC_OPTIONS = [
  { label: "Inventory Count", value: "inventory_count" },
  { label: "User Count", value: "user_count" },
  { label: "Usage Count", value: "usage_count" },
];

/** AI Inventory Simple Table Sort By Options */
export const AI_INVENTORY_SORT_BY_OPTIONS = [
  { label: "Status", value: "status" },
  { label: "Risk", value: "riskLevel" },
  { label: "Users", value: "users" },
];
